import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { apiService, WatermarkSettings } from '../services/api';
import i18n from '../i18n/config';
import { useAuth } from './AuthContext';

interface UserPreferences {
  id: string;
  user_id: string;
  theme: string;
  language: string;
  timezone: string;
  email_notifications: boolean;
  desktop_notifications: boolean;
  web_notifications: boolean;
  require_2fa: boolean;
  backup_master_keys: boolean;
  allow_multi_device_access: boolean;
  send_invite_to_unregistered_recipients?: boolean;
  session_timeout_value: number;
  session_timeout_unit: 'minutes' | 'hours' | 'days';
  screen_watermark_settings?: WatermarkSettings;
  print_watermark_settings?: WatermarkSettings;
  created_at: string;
  updated_at: string;
}

interface UserPreferencesContextType {
  preferences: UserPreferences | null;
  loading: boolean;
  error: string | null;
  refreshPreferences: () => Promise<void>;
}

const UserPreferencesContext = createContext<UserPreferencesContextType | undefined>(undefined);

export const UserPreferencesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [preferences, setPreferences] = useState<UserPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { isAuthenticated } = useAuth();

  const appliedLangRef = useRef<string | null>(null);

  const loadPreferences = useCallback(async () => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getUserPreferences();
      const prefs = response.preferences || response;
      setPreferences(prefs);
      // Apply saved language from server (source of truth after login)
      const lang = prefs?.language?.trim?.()?.toLowerCase?.();
      if (lang && lang !== appliedLangRef.current) {
        appliedLangRef.current = lang;
        const normalized = lang.split('-')[0];
        if (i18n.language?.split('-')[0]?.toLowerCase() !== normalized) {
          i18n.changeLanguage(normalized).catch(() => {});
        }
      }
    } catch (err) {
      console.error('Failed to load user preferences:', err);
      setError('Failed to load user preferences');
      // Default to enabled if we can't load preferences (e.g. not logged in)
      appliedLangRef.current = null;
      setPreferences({
        id: '',
        user_id: '',
        theme: 'light',
        language: 'en',
        timezone: 'UTC',
        email_notifications: true,
        desktop_notifications: true,
        web_notifications: true,
        require_2fa: false,
        backup_master_keys: true,
        allow_multi_device_access: true,
        session_timeout_value: 24,
        session_timeout_unit: 'hours',
        send_invite_to_unregistered_recipients: true,
        created_at: '',
        updated_at: '',
      });
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    loadPreferences();
  }, [loadPreferences]);

  const refreshPreferences = useCallback(async () => {
    await loadPreferences();
  }, [loadPreferences]);

  return (
    <UserPreferencesContext.Provider value={{ preferences, loading, error, refreshPreferences }}>
      {children}
    </UserPreferencesContext.Provider>
  );
};

export const useUserPreferences = () => {
  const context = useContext(UserPreferencesContext);
  if (context === undefined) {
    throw new Error('useUserPreferences must be used within a UserPreferencesProvider');
  }
  return context;
};

